import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { formatDecimal, formatMoney } from "../../utils/server-format.util"

const GROUP_STORAGE_KEY = "lucanto.bank.transactions.overview.grouping"
const COLLAPSE_STORAGE_KEY = "lucanto.bank.transactions.overview.collapsed"
const DEFAULT_GROUPING = "classification"
const GROUPINGS = ["classification", "counterparty", "bank_account", "source", "status"]

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`. The four
// *Template entries carry %{…} placeholders that #interpolate fills in.
const DEFAULT_TRANSLATIONS = {
  defaultTitle: "",
  hoverTitleTemplate: "",
  comparisonTemplate: "",
  netChangeLabel: "",
  moneyInLabel: "",
  moneyOutLabel: "",
  unconvertibleNoticeTemplate: "",
  nettedNoticeTemplate: ""
}

// Connects to data-controller="bank--transactions-overview"
export default class extends Controller {
  static targets = [
    "body",
    "chartTrack",
    "collapseButton",
    "comparisonText",
    "groupChart",
    "groupMenuLabel",
    "groupOption",
    "groupSelect",
    "groupTooltip",
    "moneyInValue",
    "moneyOutValue",
    "netValue",
    "nextChartButton",
    "prevChartButton",
    "summaryTitle",
    "timelineChart",
    "trendBadge",
    "trendIcon",
    "trendValue",
    "unconvertibleNotice",
    "unconvertibleNoticeText",
    "nettedNotice",
    "nettedNoticeText"
  ]

  static values = {
    translations: { type: Object, default: {} },
    overview: Object,
    currencyCode: String,
    numberFormat: { type: Object, default: {} }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.timelineChart = null
    this.groupChart = null
    this.activeGrouping = this.#storedGrouping()
    this.#applyStoredCollapse()
    this.#syncGroupSelect()
    this.#renderTimelineChart()
    this.#renderGroupChart()
    this.#setupThemeListeners()
    this.updateChartCarouselState()
    this.resizeTimeout = null
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => this.updateChartCarouselState(), 150)
    }
    window.addEventListener("resize", this.handleResize)
    this.resetSummary()
  }

  disconnect() {
    clearTimeout(this.resizeTimeout)
    this.timelineChart?.destroy()
    this.groupChart?.destroy()
    this.themeObserver?.disconnect()
    this.colorSchemeQuery?.removeEventListener?.("change", this.themeChangeHandler)
    window.removeEventListener("resize", this.handleResize)
  }

  changeGrouping(event) {
    this.applyGrouping(event.currentTarget.value || event.currentTarget.dataset.groupingValue)
  }

  applyGrouping(grouping) {
    if (!GROUPINGS.includes(grouping)) return

    this.activeGrouping = grouping
    this.#safeLocalStorageSet(GROUP_STORAGE_KEY, grouping)
    this.#syncGroupSelect()
    this.#renderGroupChart()
  }

  toggleCollapse() {
    const collapsed = !this.element.classList.contains("is-collapsed")
    this.#setCollapseState(collapsed)
    this.#safeLocalStorageSet(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0")
    if (!collapsed) {
      this.#refreshCharts()
      this.updateChartCarouselState()
    }
  }

  scrollChartsLeft() {
    if (!this.hasChartTrackTarget) return

    this.chartTrackTarget.scrollBy({ left: -this.#chartScrollStep(), behavior: "smooth" })
  }

  scrollChartsRight() {
    if (!this.hasChartTrackTarget) return

    this.chartTrackTarget.scrollBy({ left: this.#chartScrollStep(), behavior: "smooth" })
  }

  updateChartCarouselState() {
    if (!this.hasChartTrackTarget) return

    const { scrollLeft, scrollWidth, clientWidth } = this.chartTrackTarget
    const overflows = scrollWidth > clientWidth
    const atStart = scrollLeft <= 0
    const atEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth

    if (this.hasPrevChartButtonTarget) this.prevChartButtonTarget.disabled = !overflows || atStart
    if (this.hasNextChartButtonTarget) this.nextChartButtonTarget.disabled = !overflows || atEnd

    const wrapper = this.chartTrackTarget.parentElement
    wrapper?.classList.toggle("has-overflow-left", overflows && !atStart)
    wrapper?.classList.toggle("has-overflow-right", overflows && !atEnd)
  }

  resetSummary() {
    const summary = this.overviewValue.summary || {}
    if (this.hasSummaryTitleTarget) this.summaryTitleTarget.textContent = this.#texts.defaultTitle
    this.#setSummaryValues(
      summary.net_change,
      summary.money_in,
      summary.money_out,
      summary.previous_net_change,
      summary.net_change_trend
    )
    this.#restoreSummaryPopovers()
    this.#updateUnconvertibleNotice()
    this.#updateNettedNotice()
  }

  showTimelinePoint(index) {
    const point = this.#series()[index]
    if (!point) return

    if (this.hasSummaryTitleTarget) {
      this.summaryTitleTarget.textContent = this.#interpolate(this.#texts.hoverTitleTemplate, { date: point.label })
    }
    this.#setSummaryValues(point.net_change, point.money_in, point.money_out, null)
    if (this.hasComparisonTextTarget) this.comparisonTextTarget.textContent = ""
    this.#setTrendBadge(null)
    this.#clearSummaryPopovers()
  }

  clearGroupHover() {
    if (this.hasGroupTooltipTarget) this.groupTooltipTarget.hidden = true
    if (this.hasGroupTooltipTarget) this.groupTooltipTarget.removeAttribute("data-placement")
    if (this.groupChart) this.groupChart.$bankTransactionsActiveGroupIndex = null
    this.groupChart?.draw?.()
  }

  // number_to_currency(value, currency: report currency) — the server's
  // string for the figure it rendered (number_format_data).
  formatAmount(value) {
    const currency = this.overviewValue?.report_currency || this.currencyCodeValue
    return formatMoney(value, currency, this.numberFormatValue)
  }

  #renderTimelineChart() {
    if (!this.hasTimelineChartTarget) return

    this.timelineChart?.destroy()
    const series = this.#series()
    const context = this.timelineChartTarget.getContext("2d")
    if (!context) return

    const incomeGradient = context.createLinearGradient(0, 0, 0, this.timelineChartTarget.height || 160)
    incomeGradient.addColorStop(0, this.#cssValue("--bank-overview-income-fill-start"))
    incomeGradient.addColorStop(1, this.#cssValue("--bank-overview-income-fill-end"))

    const outGradient = context.createLinearGradient(0, 0, 0, this.timelineChartTarget.height || 160)
    outGradient.addColorStop(0, this.#cssValue("--bank-overview-out-fill-start"))
    outGradient.addColorStop(1, this.#cssValue("--bank-overview-out-fill-end"))

    const incomeColor = this.#cssValue("--bank-overview-income")
    const outColor = this.#cssValue("--bank-overview-out")
    const gridColor = this.#withAlpha(this.#cssValue("--bank-overview-grid"), 0.6)
    const axisColor = this.#cssValue("--bank-overview-axis")
    const guideColor = this.#cssValue("--bank-overview-hover-guide")
    const rulerColor = this.#cssValue("--bank-overview-hover-ruler")
    const pointBackgroundColor = this.#cssValue("--bank-overview-chart-point-bg")
    const tooltipBackgroundColor = this.#cssValue("--bank-overview-tooltip-bg")
    const tooltipBorderColor = this.#cssValue("--bank-overview-tooltip-border")

    this.timelineChart = new Chart(context, {
      type: "line",
      data: {
        labels: series.map((point) => point.label),
        datasets: [
          {
            label: this.#texts.moneyInLabel,
            data: series.map((point) => Number(point.cumulative_money_in || 0)),
            borderColor: incomeColor,
            borderWidth: 2,
            backgroundColor: incomeGradient,
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBorderWidth: 2,
            pointHoverBackgroundColor: pointBackgroundColor,
            pointHoverBorderColor: incomeColor,
            tension: 0.22
          },
          {
            label: this.#texts.moneyOutLabel,
            data: series.map((point) => Number(point.cumulative_money_out || 0)),
            borderColor: outColor,
            borderWidth: 2,
            backgroundColor: outGradient,
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBorderWidth: 2,
            pointHoverBackgroundColor: pointBackgroundColor,
            pointHoverBorderColor: outColor,
            tension: 0.22
          }
        ]
      },
      plugins: [
        this.#timelineHoverGuidePlugin({
          guideColor,
          rulerColor,
          tooltipBackgroundColor,
          tooltipBorderColor,
          pointBackgroundColor
        })
      ],
      options: {
        animation: this.#prefersReducedMotion() ? false : { duration: 420 },
        interaction: { mode: "index", intersect: false },
        maintainAspectRatio: false,
        responsive: true,
        onHover: (_event, elements) => {
          if (elements?.length > 0) this.showTimelinePoint(elements[0].index)
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        layout: {
          padding: { top: 2, right: 2, bottom: 0, left: 2 }
        },
        scales: {
          x: {
            grid: {
              color: (context) => (context.index === 0 ? "transparent" : gridColor),
              drawTicks: false
            },
            ticks: { color: axisColor, maxTicksLimit: 5, font: { size: 11 }, padding: 7 },
            border: { display: false }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: (context) => (this.#isTopGridLine(context) ? "transparent" : gridColor),
              drawTicks: false
            },
            ticks: {
              color: axisColor,
              maxTicksLimit: 3,
              padding: 3,
              callback: (value) => this.#compactAmount(value)
            },
            afterFit: (scale) => {
              scale.width = Math.min(scale.width, 42)
            },
            border: { display: false }
          }
        }
      }
    })
  }

  #timelineHoverGuidePlugin({
    guideColor,
    rulerColor,
    tooltipBackgroundColor,
    tooltipBorderColor,
    pointBackgroundColor
  }) {
    return {
      id: "bankTransactionsTimelineHoverGuide",
      afterDraw: (chart) => {
        const activeElements = chart.getActiveElements?.() || []
        if (activeElements.length === 0) return

        const points = activeElements
          .filter((activeElement) => activeElement?.element && activeElement.datasetIndex !== undefined)
          .sort((a, b) => a.datasetIndex - b.datasetIndex)
        if (points.length === 0) return

        const { ctx, chartArea } = chart
        const rulerX = points[0].element.x

        ctx.save()
        ctx.lineWidth = 1
        ctx.strokeStyle = rulerColor
        ctx.setLineDash([])
        ctx.beginPath()
        ctx.moveTo(rulerX, chartArea.top)
        ctx.lineTo(rulerX, chartArea.bottom)
        ctx.stroke()

        const chipLayouts = this.#timelineHoverChipLayouts({ chart, points, chartArea })

        points.forEach((activeElement, position) => {
          const { datasetIndex, element } = activeElement
          const dataset = chart.data.datasets[datasetIndex]
          const y = element.y
          const chip = chipLayouts[position]

          ctx.strokeStyle = guideColor
          ctx.lineWidth = 1
          ctx.setLineDash([5, 5])
          ctx.beginPath()
          ctx.moveTo(chip ? chip.x + chip.width + 6 : chartArea.left, y)
          ctx.lineTo(rulerX, y)
          ctx.stroke()

          ctx.setLineDash([])
          if (chip) {
            this.#drawHoverValueChip({
              ctx,
              chip,
              fillColor: tooltipBackgroundColor,
              borderColor: tooltipBorderColor,
              textColor: dataset?.borderColor || "#fff"
            })
          }

          ctx.beginPath()
          ctx.arc(element.x, y, 4.5, 0, Math.PI * 2)
          ctx.fillStyle = pointBackgroundColor
          ctx.fill()
          ctx.lineWidth = 2
          ctx.strokeStyle = dataset?.borderColor || axisColor
          ctx.stroke()
        })

        ctx.restore()
      }
    }
  }

  #timelineHoverChipLayouts({ chart, points, chartArea }) {
    const { ctx } = chart
    const chipHeight = 20
    const chipGap = 4
    const chipPaddingX = 8
    const chipX = chartArea.left + 8
    const minY = chartArea.top + 2
    const maxY = chartArea.bottom - chipHeight - 2

    ctx.font = "600 12px Inter, system-ui, sans-serif"
    const layouts = points
      .map((activeElement, position) => {
        const dataset = chart.data.datasets[activeElement.datasetIndex]
        const label = this.#compactAmount(dataset?.data?.[activeElement.index])
        const measuredWidth = ctx.measureText?.(label)?.width || label.length * 7

        return {
          position,
          label,
          x: chipX,
          y: this.#clamp(activeElement.element.y - chipHeight / 2, minY, maxY),
          width: Math.ceil(measuredWidth) + chipPaddingX * 2,
          height: chipHeight,
          radius: 6
        }
      })
      .sort((a, b) => a.y - b.y)

    layouts.forEach((layout, index) => {
      if (index === 0) return

      const previous = layouts[index - 1]
      layout.y = Math.max(layout.y, previous.y + previous.height + chipGap)
    })

    const overflow = layouts.length > 0 ? layouts.at(-1).y + chipHeight - maxY : 0
    if (overflow > 0) {
      layouts.forEach((layout) => {
        layout.y = Math.max(minY, layout.y - overflow)
      })
    }

    return layouts.sort((a, b) => a.position - b.position)
  }

  #drawHoverValueChip({ ctx, chip, fillColor, borderColor, textColor }) {
    ctx.save()
    ctx.setLineDash([])
    ctx.fillStyle = fillColor
    ctx.strokeStyle = borderColor
    ctx.lineWidth = 1
    this.#roundedRect(ctx, chip.x, chip.y, chip.width, chip.height, chip.radius)
    ctx.fill()
    ctx.stroke()

    ctx.fillStyle = textColor
    ctx.font = "600 12px Inter, system-ui, sans-serif"
    ctx.textAlign = "left"
    ctx.textBaseline = "middle"
    ctx.fillText(chip.label, chip.x + 8, chip.y + chip.height / 2)
    ctx.restore()
  }

  #groupFullHeightHoverPlugin(groups) {
    return {
      id: "bankTransactionsGroupFullHeightHover",
      beforeEvent: (_chart, args) => {
        const event = args?.event
        if (!event) return

        if (event.type === "mouseout") {
          this.clearGroupHover()
          return
        }

        if (event.type !== "mousemove") return

        const activeIndex = this.#groupIndexFromChartX(this.groupChart, event.x, groups)
        if (activeIndex === null || activeIndex === undefined) {
          this.clearGroupHover()
          return
        }

        this.#handleGroupHover({ native: { offsetX: event.x, offsetY: event.y } }, [{ index: activeIndex }], groups)
      }
    }
  }

  #groupHoverBandPlugin({ hoverBandColor }) {
    return {
      id: "bankTransactionsGroupHoverBand",
      beforeDatasetsDraw: (chart) => {
        const activeElements = chart.getActiveElements?.() || []
        const activeElement = activeElements.find((element) => element?.element && element.index !== undefined)
        const activeIndex = activeElement?.index ?? chart.$bankTransactionsActiveGroupIndex
        if (activeIndex === null || activeIndex === undefined) return

        const { ctx, chartArea } = chart
        const labelCount = Math.max(chart.data.labels?.length || 1, 1)
        const categoryWidth = chartArea.width / labelCount
        const bandWidth = Math.max(24, categoryWidth * 0.78)
        const bandCenter = activeElement?.element?.x ?? this.#groupPixelForIndex(chart, activeIndex)
        const x = this.#clamp(bandCenter - bandWidth / 2, chartArea.left, chartArea.right - bandWidth)
        const y = chartArea.top + 2
        const height = Math.max(0, chartArea.bottom - chartArea.top - 4)

        ctx.save()
        ctx.fillStyle = hoverBandColor
        this.#roundedRect(ctx, x, y, bandWidth, height, 6)
        ctx.fill()
        ctx.restore()
      }
    }
  }

  #renderGroupChart() {
    if (!this.hasGroupChartTarget) return

    this.groupChart?.destroy()
    const groups = this.#groupsForActiveGrouping()
    const context = this.groupChartTarget.getContext("2d")
    if (!context) return

    const incomeColor = this.#cssValue("--bank-overview-income")
    const outColor = this.#cssValue("--bank-overview-out")
    const incomeBarColor = this.#cssValue("--bank-overview-income-bar")
    const outBarColor = this.#cssValue("--bank-overview-out-bar")
    const gridColor = this.#withAlpha(this.#cssValue("--bank-overview-grid"), 0.6)
    const axisColor = this.#cssValue("--bank-overview-axis")
    const labelColor = this.#cssValue("--bank-overview-axis-strong")
    const hoverBandColor = this.#cssValue("--bank-overview-hover-band")
    const labelSlotWidth = groups.length > 0 ? (this.groupChartTarget.clientWidth || 420) / groups.length : 80
    const labelLimit = Math.max(5, Math.min(14, Math.floor(labelSlotWidth / 7.2)))
    const scaleBounds = this.#groupScaleBounds(groups)

    this.groupChart = new Chart(context, {
      type: "bar",
      data: {
        labels: groups.map((group) => group.label),
        datasets: [
          {
            label: this.#texts.moneyInLabel,
            data: groups.map((group) => Number(group.money_in || 0)),
            backgroundColor: incomeBarColor,
            borderColor: incomeColor,
            borderWidth: { top: 2, right: 0, bottom: 0, left: 0 },
            borderRadius: 2,
            barPercentage: 0.6,
            categoryPercentage: 0.72,
            minBarLength: 3
          },
          {
            label: this.#texts.moneyOutLabel,
            data: groups.map((group) => -Number(group.money_out || 0)),
            backgroundColor: outBarColor,
            borderColor: outColor,
            borderWidth: { top: 0, right: 0, bottom: 2, left: 0 },
            borderRadius: 2,
            barPercentage: 0.6,
            categoryPercentage: 0.72,
            minBarLength: 3
          }
        ]
      },
      plugins: [this.#groupFullHeightHoverPlugin(groups), this.#groupHoverBandPlugin({ hoverBandColor })],
      options: {
        animation: this.#prefersReducedMotion() ? false : { duration: 360 },
        interaction: { mode: "index", intersect: false, axis: "x" },
        hover: { mode: "index", intersect: false, axis: "x" },
        maintainAspectRatio: false,
        responsive: true,
        onHover: (event, elements) => this.#handleGroupHover(event, elements, groups),
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        },
        layout: {
          padding: { top: 4, right: 2, bottom: 0, left: 0 }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              autoSkip: false,
              callback: (_value, index) => this.#groupTickLabel(groups[index]?.label, labelLimit),
              color: labelColor,
              font: { size: 10, weight: "500" },
              maxRotation: 0,
              padding: 4
            },
            border: { display: false }
          },
          y: {
            grid: {
              color: (context) => (this.#isTopGridLine(context) ? "transparent" : gridColor),
              drawTicks: false
            },
            max: scaleBounds.max,
            min: scaleBounds.min,
            afterBuildTicks: (scale) => {
              scale.ticks = [{ value: scaleBounds.min }, { value: 0 }, { value: scaleBounds.max }]
            },
            ticks: {
              autoSkip: false,
              color: axisColor,
              maxTicksLimit: 3,
              callback: (value) => this.#compactAmount(value)
            },
            border: { display: false }
          }
        }
      }
    })
  }

  #setSummaryValues(netChange, moneyIn, moneyOut, comparisonNet, trendData = null) {
    const net = Number(netChange || 0)
    if (this.hasNetValueTarget) {
      this.netValueTarget.textContent = this.formatAmount(net)
      this.netValueTarget.dataset.value = String(net)
      this.netValueTarget.classList.toggle("is-negative", net < 0)
      this.netValueTarget.classList.toggle("is-positive", net >= 0)
    }
    if (this.hasMoneyInValueTarget) this.moneyInValueTarget.textContent = this.formatAmount(moneyIn)
    if (this.hasMoneyOutValueTarget) this.moneyOutValueTarget.textContent = `-${this.formatAmount(moneyOut)}`

    if (this.hasComparisonTextTarget && comparisonNet !== null && comparisonNet !== undefined) {
      this.comparisonTextTarget.textContent = this.#interpolate(this.#texts.comparisonTemplate, {
        amount: this.formatAmount(comparisonNet)
      })
    } else if (this.hasComparisonTextTarget) {
      this.comparisonTextTarget.textContent = ""
    }

    this.#setTrendBadge(trendData)
  }

  #setTrendBadge(trendData) {
    const showTrend = trendData?.reliable === true && trendData?.percentage != null
    const direction = trendData?.direction

    if (this.hasTrendBadgeTarget) {
      this.trendBadgeTarget.classList.toggle("is-hidden", !showTrend)
      this.trendBadgeTarget.classList.remove("is-positive", "is-negative", "is-neutral")
      this.trendBadgeTarget.classList.add(this.#trendStateClass(direction))
    }

    if (this.hasTrendValueTarget) {
      this.trendValueTarget.textContent = showTrend ? `${trendData.percentage}%` : ""
    }

    if (this.hasTrendIconTarget) {
      this.trendIconTarget.textContent = showTrend ? this.#trendIconName(direction) : ""
    }
  }

  #trendStateClass(direction) {
    if (direction === "up") return "is-positive"
    if (direction === "down") return "is-negative"
    return "is-neutral"
  }

  #trendIconName(direction) {
    if (direction === "down") return "south_east"
    if (direction === "neutral") return "east"
    return "north_east"
  }

  #restoreSummaryPopovers() {
    const elements = [
      this.hasNetValueTarget ? this.netValueTarget : null,
      this.hasMoneyInValueTarget ? this.moneyInValueTarget : null,
      this.hasMoneyOutValueTarget ? this.moneyOutValueTarget : null
    ]
    elements.forEach((element) => {
      if (!element || element.dataset.defaultPopoverContent === undefined) return

      element.dataset.popoverContentValue = element.dataset.defaultPopoverContent
    })
  }

  #clearSummaryPopovers() {
    const elements = [
      this.hasNetValueTarget ? this.netValueTarget : null,
      this.hasMoneyInValueTarget ? this.moneyInValueTarget : null,
      this.hasMoneyOutValueTarget ? this.moneyOutValueTarget : null
    ]
    elements.forEach((element) => {
      if (!element || element.dataset.popoverContentValue === undefined) return

      element.dataset.popoverContentValue = ""
    })
  }

  #updateUnconvertibleNotice() {
    if (!this.hasUnconvertibleNoticeTarget) return

    const summary = this.overviewValue.summary || {}
    const currencies = Array.isArray(summary.unconvertible_currencies)
      ? summary.unconvertible_currencies.filter(Boolean)
      : []
    const show = Number(summary.unconvertible_count || 0) > 0 && currencies.length > 0

    this.unconvertibleNoticeTarget.hidden = !show
    if (!show) return

    const text = this.#interpolate(this.#texts.unconvertibleNoticeTemplate, { currencies: currencies.join(", ") })
    if (this.hasUnconvertibleNoticeTextTarget) {
      this.unconvertibleNoticeTextTarget.textContent = text
    } else {
      this.unconvertibleNoticeTarget.textContent = text
    }
  }

  // Netted-out pairs (a charge and the refund that cancelled it) stay in the list but
  // are left out of every total, so the overview says how many and for how much —
  // otherwise the sums simply look wrong against the rows on screen.
  #updateNettedNotice() {
    if (!this.hasNettedNoticeTarget) return

    const summary = this.overviewValue.summary || {}
    const count = Number(summary.netted_transactions_count || 0)

    this.nettedNoticeTarget.hidden = count <= 0
    if (count <= 0) return

    const text = this.#interpolate(this.#texts.nettedNoticeTemplate, {
      count: String(count),
      amount: this.formatAmount(summary.netted_amount)
    })
    if (this.hasNettedNoticeTextTarget) {
      this.nettedNoticeTextTarget.textContent = text
    } else {
      this.nettedNoticeTarget.textContent = text
    }
  }

  #handleGroupHover(event, elements, groups) {
    const nativeEvent = event?.native || event
    const activeIndex = elements?.[0]?.index ?? this.#groupIndexFromHoverEvent(nativeEvent, groups)

    if (!this.hasGroupTooltipTarget || activeIndex === null || activeIndex === undefined) {
      this.clearGroupHover()
      return
    }

    const group = groups[activeIndex]
    if (!group) return

    this.#ensureGroupTooltipLayer()
    if (this.groupChart) this.groupChart.$bankTransactionsActiveGroupIndex = activeIndex
    this.groupTooltipTarget.innerHTML = this.#groupTooltipHtml(group)
    this.groupTooltipTarget.hidden = false

    if (nativeEvent?.offsetX != null) {
      this.#positionGroupTooltip(activeIndex, nativeEvent)
    }

    this.groupChart?.draw?.()
  }

  #groupTooltipHtml(group) {
    return `
      <div class="bank-transactions-overview__group-tooltip-title">${this.#escapeHtml(group.label)}</div>
      <div class="bank-transactions-overview__group-tooltip-row">
        <span>${this.#escapeHtml(this.#texts.netChangeLabel || "Net change")}</span>
        <strong class="bank-transactions-overview__group-tooltip-value">${this.#escapeHtml(this.formatAmount(group.net_change))}</strong>
      </div>
      <div class="bank-transactions-overview__group-tooltip-divider"></div>
      <div class="bank-transactions-overview__group-tooltip-row bank-transactions-overview__group-tooltip-row--keyed">
        <span class="bank-transactions-overview__group-tooltip-key bank-transactions-overview__group-tooltip-key--in"></span>
        <span>${this.#escapeHtml(this.#texts.moneyInLabel)}</span>
        <strong class="bank-transactions-overview__group-tooltip-value">${this.#escapeHtml(this.formatAmount(group.money_in))}</strong>
      </div>
      <div class="bank-transactions-overview__group-tooltip-row bank-transactions-overview__group-tooltip-row--keyed">
        <span class="bank-transactions-overview__group-tooltip-key bank-transactions-overview__group-tooltip-key--out"></span>
        <span>${this.#escapeHtml(this.#texts.moneyOutLabel)}</span>
        <strong class="bank-transactions-overview__group-tooltip-value">${this.#escapeHtml(this.formatAmount(group.money_out))}</strong>
      </div>
    `
  }

  #positionGroupTooltip(activeIndex, nativeEvent) {
    const tooltip = this.groupTooltipTarget
    const chart = this.groupChart
    const chartArea = chart?.chartArea
    if (!tooltip || !chart || !chartArea) return

    const gap = 12
    const inset = 8
    const tooltipWidth = tooltip.offsetWidth || 240
    const tooltipHeight = tooltip.offsetHeight || 120
    const categoryWidth = chartArea.width / Math.max(chart.data.labels?.length || 1, 1)
    const triggerCenterX = this.#groupPixelForIndex(chart, activeIndex)
    const rootRect = this.element.getBoundingClientRect()
    const chartRect = this.groupChartTarget.getBoundingClientRect()
    const rootWidth = rootRect.width || this.groupChartTarget.clientWidth
    const chartLeft = chartRect.left - rootRect.left
    const chartTop = chartRect.top - rootRect.top
    const triggerLeft = chartLeft + Math.max(chartArea.left, triggerCenterX - categoryWidth / 2)
    const triggerRight = chartLeft + Math.min(chartArea.right, triggerCenterX + categoryWidth / 2)
    const triggerCenter = chartLeft + triggerCenterX
    const preferredTop =
      chartTop +
      (Number.isFinite(nativeEvent?.offsetY)
        ? nativeEvent.offsetY - tooltipHeight / 2
        : chartArea.top + (chartArea.height - tooltipHeight) / 2)
    const viewportBounds = {
      minX: inset,
      maxX: Math.max(inset, rootWidth - tooltipWidth - inset),
      minY: inset - rootRect.top,
      maxY: Math.max(inset - rootRect.top, window.innerHeight - rootRect.top - tooltipHeight - inset)
    }
    const bounds = {
      minX: viewportBounds.minX,
      maxX: viewportBounds.maxX,
      minY: viewportBounds.minY,
      maxY: viewportBounds.maxY
    }
    const placements = [
      { name: "right", left: triggerRight + gap, top: preferredTop, fits: triggerRight + gap <= bounds.maxX },
      {
        name: "left",
        left: triggerLeft - gap - tooltipWidth,
        top: preferredTop,
        fits: triggerLeft - gap - tooltipWidth >= bounds.minX
      },
      {
        name: "top",
        left: triggerCenter - tooltipWidth / 2,
        top: chartTop + chartArea.top - gap - tooltipHeight,
        fits: chartTop + chartArea.top - gap - tooltipHeight >= bounds.minY
      },
      {
        name: "bottom",
        left: triggerCenter - tooltipWidth / 2,
        top: chartTop + chartArea.bottom + gap,
        fits: chartTop + chartArea.bottom + gap + tooltipHeight <= bounds.maxY
      }
    ]
    const placement = placements.find((candidate) => candidate.fits) || placements[0]

    tooltip.style.left = `${this.#clamp(placement.left, bounds.minX, bounds.maxX)}px`
    tooltip.style.top = `${this.#clamp(placement.top, bounds.minY, bounds.maxY)}px`
    tooltip.dataset.placement = placement.name
  }

  #ensureGroupTooltipLayer() {
    if (!this.hasGroupTooltipTarget || this.groupTooltipTarget.parentElement === this.element) return

    this.element.appendChild(this.groupTooltipTarget)
  }

  #groupIndexFromHoverEvent(nativeEvent, groups) {
    if (!nativeEvent || nativeEvent.offsetX === null || nativeEvent.offsetX === undefined || groups.length === 0)
      return null

    return this.#groupIndexFromChartX(this.groupChart, nativeEvent.offsetX, groups)
  }

  #groupIndexFromChartX(chart, x, groups) {
    if (!chart || x === null || x === undefined || groups.length === 0) return null

    const xScale = chart.scales?.x
    if (xScale?.getValueForPixel) {
      const value = Math.round(Number(xScale.getValueForPixel(x)))
      if (value >= 0 && value < groups.length) return value
    }

    const chartArea = chart.chartArea
    if (!chartArea || x < chartArea.left || x > chartArea.right) return null

    const ratio = (x - chartArea.left) / Math.max(chartArea.width, 1)
    return this.#clamp(Math.floor(ratio * groups.length), 0, groups.length - 1)
  }

  #groupPixelForIndex(chart, index) {
    const xScale = chart.scales?.x
    if (xScale?.getPixelForValue) return xScale.getPixelForValue(index)

    const { chartArea } = chart
    const labelCount = Math.max(chart.data.labels?.length || 1, 1)
    const categoryWidth = chartArea.width / labelCount
    return chartArea.left + categoryWidth * (index + 0.5)
  }

  #series() {
    return Array.isArray(this.overviewValue.series) ? this.overviewValue.series : []
  }

  #groupsForActiveGrouping() {
    const groups = this.overviewValue.groups || {}
    return Array.isArray(groups[this.activeGrouping]) ? groups[this.activeGrouping] : []
  }

  #groupScaleBounds(groups) {
    const positiveMax = Math.max(0, ...groups.map((group) => Number(group.money_in || 0)))
    const negativeMax = Math.max(0, ...groups.map((group) => Number(group.money_out || 0)))
    const fallbackMax = Math.max(positiveMax, negativeMax, 1) * 0.04
    const minimumAxisShare = 0.18
    let positiveLimit = this.#tightAxisLimit(positiveMax || fallbackMax)
    let negativeLimit = this.#tightAxisLimit(negativeMax || fallbackMax)
    const minimumVisibleLimit = this.#tightAxisLimit(Math.max(positiveLimit, negativeLimit) * minimumAxisShare)

    positiveLimit = Math.max(positiveLimit, minimumVisibleLimit)
    negativeLimit = Math.max(negativeLimit, minimumVisibleLimit)

    return {
      max: positiveLimit,
      min: -negativeLimit
    }
  }

  #tightAxisLimit(value) {
    const numeric = Number(value || 0)
    if (numeric <= 0) return 1

    const magnitude = 10 ** Math.floor(Math.log10(numeric))
    const nextMagnitude = magnitude * 10
    if (numeric >= nextMagnitude * 0.9) return nextMagnitude

    const step = magnitude / 10
    const limit = Math.ceil(numeric / step) * step

    return limit === numeric ? limit + step : limit
  }

  #storedGrouping() {
    const stored = this.#safeLocalStorageGet(GROUP_STORAGE_KEY)
    return GROUPINGS.includes(stored) ? stored : DEFAULT_GROUPING
  }

  #syncGroupSelect() {
    if (this.hasGroupSelectTarget) this.groupSelectTarget.value = this.activeGrouping

    this.groupOptionTargets.forEach((option) => {
      const active = option.dataset.groupingValue === this.activeGrouping
      option.classList.toggle("is-active", active)
      option.setAttribute("aria-checked", active ? "true" : "false")
      option.querySelector(".bank-transactions-overview__group-menu-check").textContent = active ? "check" : ""

      if (active && this.hasGroupMenuLabelTarget) {
        this.groupMenuLabelTarget.textContent = option.dataset.groupingLabel || option.textContent.trim()
      }
    })
  }

  #applyStoredCollapse() {
    const collapsed = this.#safeLocalStorageGet(COLLAPSE_STORAGE_KEY) === "1"
    this.#setCollapseState(collapsed)
  }

  #setCollapseState(collapsed) {
    this.element.classList.toggle("is-collapsed", collapsed)
    this.collapseButtonTargets.forEach((button) => {
      button.setAttribute("aria-expanded", collapsed ? "false" : "true")
    })
  }

  #cssValue(name) {
    const value = getComputedStyle(this.element).getPropertyValue(name).trim()
    if (value) return value

    // The --bank-overview-* tokens are defined on the component root for every
    // theme, so an empty value means the stylesheet is missing or misnamed.
    // Warn (dev signal) and return a neutral that never throws in canvas
    // (addColorStop("") raises SyntaxError) rather than embedding theme colors here.
    console.warn(`[bank--transactions-overview] CSS variable ${name} is not defined; using transparent`)
    return "transparent"
  }

  #withAlpha(color, alphaMultiplier) {
    const rgbaMatch = String(color).match(/^rgba?\(([^)]+)\)$/i)
    if (rgbaMatch) {
      const parts = rgbaMatch[1].split(",").map((part) => part.trim())
      const alpha = parts[3] === undefined ? 1 : Number.parseFloat(parts[3])
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${Math.max(0, Math.min(1, alpha * alphaMultiplier))})`
    }

    return color
  }

  #isTopGridLine(context) {
    const ticks = context.chart?.scales?.[context.scale?.id]?.ticks
    if (!Array.isArray(ticks) || ticks.length === 0) return false

    const maxValue = Math.max(...ticks.map((tick) => Number(tick.value || 0)))
    return Number(context.tick?.value || 0) === maxValue
  }

  #setupThemeListeners() {
    this.themeChangeHandler = () => this.#refreshCharts()
    this.themeObserver = new MutationObserver(this.themeChangeHandler)

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-theme", "data-bs-theme"]
    })

    if (document.body) {
      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class", "data-theme", "data-bs-theme"]
      })
    }

    this.colorSchemeQuery = window.matchMedia?.("(prefers-color-scheme: dark)")
    this.colorSchemeQuery?.addEventListener?.("change", this.themeChangeHandler)
  }

  #refreshCharts() {
    const schedule = window.requestAnimationFrame || ((callback) => callback())
    schedule(() => {
      this.#renderTimelineChart()
      this.#renderGroupChart()
      this.updateChartCarouselState()
    })
  }

  #chartScrollStep() {
    if (!this.hasChartTrackTarget) return 240

    return Math.max(240, this.chartTrackTarget.clientWidth * 0.75)
  }

  #compactAmount(value) {
    const numeric = Number(value || 0)
    const abs = Math.abs(numeric)
    if (abs >= 1_000_000) return `${this.#trimNumber(numeric / 1_000_000)}M`
    if (abs >= 1_000) return `${this.#trimNumber(numeric / 1_000)}K`
    return this.#trimNumber(numeric)
  }

  #trimNumber(value) {
    return formatDecimal(value, this.numberFormatValue, { maximumFractionDigits: 1 })
  }

  #truncateLabel(value, maxLength) {
    const label = String(value || "")
    if (label.length <= maxLength) return label

    return `${label.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}…`
  }

  #groupTickLabel(value, maxLength) {
    const label = String(value || "")
    if (label.length <= maxLength) return label

    const words = label.split(/\s+/).filter(Boolean)
    if (words.length > 1 && maxLength >= 8) {
      const lineLength = Math.max(4, Math.min(10, Math.floor(maxLength * 0.75)))

      return [this.#truncateLabel(words[0], lineLength), this.#truncateLabel(words.slice(1).join(" "), lineLength)]
    }

    return this.#truncateLabel(label, maxLength)
  }

  #roundedRect(ctx, x, y, width, height, radius) {
    if (typeof ctx.roundRect === "function") {
      ctx.beginPath()
      ctx.roundRect(x, y, width, height, radius)
      return
    }

    const clampedRadius = Math.min(radius, width / 2, height / 2)
    ctx.beginPath()
    ctx.moveTo(x + clampedRadius, y)
    ctx.lineTo(x + width - clampedRadius, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + clampedRadius)
    ctx.lineTo(x + width, y + height - clampedRadius)
    ctx.quadraticCurveTo(x + width, y + height, x + width - clampedRadius, y + height)
    ctx.lineTo(x + clampedRadius, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - clampedRadius)
    ctx.lineTo(x, y + clampedRadius)
    ctx.quadraticCurveTo(x, y, x + clampedRadius, y)
  }

  #clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  #interpolate(template, values) {
    return String(template || "").replace(/%\{([^}]+)\}/g, (_match, key) => values[key] ?? "")
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  #prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  }

  #safeLocalStorageGet(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (_error) {
      return null
    }
  }

  #safeLocalStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (_error) {
      // no-op
    }
  }
}
